import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { Field } from 'formik'
import { isEmpty, isNil } from 'lodash'
import { IconFlask, IconInfoCircle, IconPlayerPlay } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { Banner } from '@/components/banners/Banner'
import { Drawer } from '@/components/drawer/Drawer'
import {
  fetchCallBelongsToPlaybook,
  fetchSavedCallNote,
  recentCallsScope,
  testBitFlow,
} from '@/reducers/playbooks/playbook.actions'
import { CHANNEL_TYPE } from '@/utils/constants'
import { TextField } from '@/components/forms/formik'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'

import { buildInlineTaxonomies, getRowTypeKey, normalizeFieldKey } from '../helpers'
import { buildPreviousValues } from '../helpers/noteComparison.helpers'
import { AgentSummaryView } from './AgentSummaryView'
import { RecentCallsList } from './RecentCallsList'

// Results are grouped under the same visibility headers as the editor on the
// call channel (the only channel that honors show_to_user).
const RESULT_GROUPS = [
  { id: 'shown-to-agent', title: 'Shown to Agent', isShown: true },
  { id: 'processed-later', title: 'Processed Later', isShown: false },
]

// Testing lives in a drawer off the editor rather than a section below it: a run
// is about the configuration as a whole, so it's reached from the action bar
// alongside Discard and Update, and the fields stay on screen behind it.
//
// The drawer is deliberately not URL-synced — PlaybookForm mirrors the page's
// org/channel/playbook selection into the query string, and a drawer id would
// wipe it.
//
// Notetaker is bit-flow only and the action bar mounts only alongside a loaded
// flow, so this always has one to test and testing always goes through the
// bit-flow test endpoint.
export const TestPromptForm = ({ values, setFieldValue, hasErrors = false }) => {
  const dispatch = useDispatch()
  // A test has to run the same edges a save would send, tokens included.
  const { notetakerBranchingEnabled: branchingEnabled = false } = useFlags()
  const {
    rtnOrg,
    rtnRecentCalls = [],
    rtnRecentCallsScope,
  } = useSelector((state) => state.playbook)
  const currentUserOrgId = useSelector((state) => state.currentUser?.organizationid)
  const {
    loading: { promptTests: promptTestsLoading },
  } = useSelector((state) => state.prompts)
  const [testResponse, setTestResponse] = useState(null)
  // Keys the result views, so each run's results start as a fresh read rather
  // than inheriting what the reader opened on the last one — an original answer
  // or the skipped fields, for a field that may not have changed this time.
  const [runCount, setRunCount] = useState(0)
  // The note the live configuration wrote for the call the test just ran on, so
  // each field can be read against the answer it produces today — and whether
  // that call is one this playbook ran, since a note from another playbook's
  // configuration isn't a "before" for anything edited here.
  const [savedNote, setSavedNote] = useState(null)
  const [noteIsFromAnotherPlaybook, setNoteIsFromAnotherPlaybook] = useState(false)

  const isMessage = values.selected_channel !== CHANNEL_TYPE.CALL
  const organizationId = rtnOrg || currentUserOrgId
  // Only a call-channel flow is scoped to a playbook; the others are configured
  // per channel for the whole org (see getSummaryConfigForPlaybook), so the
  // playbook still sitting in the selector isn't a scope for them. Passing it
  // would narrow both the recent-calls search and the membership check to a
  // playbook the interaction was never going to belong to.
  const scopedPlaybookCid = isMessage ? '' : values.selected_playbook_cid
  const label = isMessage ? 'Message ID' : 'Call ID'
  const placeholder = 'e.g. 8842013'
  // Don't test a config that can't be saved; the prompt-error message takes
  // precedence over the missing-ID hint.
  const tooltipMessage = hasErrors
    ? 'Resolve the highlighted prompt errors before testing'
    : `You must add a ${isMessage ? 'message ID' : 'call ID'}`

  const handleTestRTNPrompt = async () => {
    // The editor resolves a legacy prompt's model name to an id when it loads
    // (see RowEditorForm), so the form's id is the whole answer here.
    const selectedModelId = values.llm_model_id ?? null

    // A call picked from the recent-calls list is already known to be one of
    // this configuration's own, so asking the API again would be a round trip
    // for an answer we have — but only if the list on screen was fetched for the
    // configuration on screen. An in-flight or superseded result still sitting
    // in the store belongs to a different playbook, and trusting it would skip
    // the very check that keeps another playbook's note out of the comparison.
    const listMatchesScope =
      rtnRecentCallsScope ===
      recentCallsScope({ playbookCid: scopedPlaybookCid, callType: values.selected_channel, organizationId }) // prettier-ignore
    const isKnownPlaybookCall =
      listMatchesScope &&
      rtnRecentCalls.some((call) => String(call.call_id) === String(values.call_id))

    // The saved note is fetched alongside the run, not after it: it's an
    // independent read, and sequencing it would add its latency to a test that
    // already takes seconds.
    const [response, note, belongsToPlaybook] = await Promise.all([
      dispatch(
        testBitFlow({
          callId: values.call_id,
          promptFields: values.prompt_fields,
          llmModelId: selectedModelId,
          // Unsaved shared-prompt edits so the test runs against what's on screen.
          systemPrompt: values.system_prompt,
          userPrompt: values.user_prompt,
          maxResponseTokens: values.max_response_tokens,
          // Unsaved taxonomy edits (see PromptFieldRows) in the endpoint's
          // inline_taxonomies shape, keyed by taxonomy_uuid.
          inlineTaxonomies: buildInlineTaxonomies(values.pending_taxonomies),
          // Gate the test run exactly like production, so a skipped field here
          // is a skipped field there.
          fieldLinks: values.field_links,
          branchingEnabled,
        })
      ),
      dispatch(fetchSavedCallNote(values.call_id)),
      isKnownPlaybookCall
        ? true
        : dispatch(
            fetchCallBelongsToPlaybook({
              callId: values.call_id,
              playbookCid: scopedPlaybookCid,
              callType: values.selected_channel,
              organizationId,
            })
          ),
    ])

    setTestResponse(response)
    setRunCount((count) => count + 1)
    setSavedNote(note)
    // Only a definite "no" suppresses the comparison; "couldn't tell" (null)
    // leaves it alone.
    setNoteIsFromAnotherPlaybook(belongsToPlaybook === false)
  }

  // Only the fields the saved note and this run have in common; a field added
  // since the note was written has nothing to be compared against. A note
  // belonging to another playbook is set aside entirely rather than matched on
  // field names the two happen to share.
  const previousValues =
    noteIsFromAnotherPlaybook || isNil(testResponse?.parsed_response)
      ? {}
      : buildPreviousValues(testResponse.parsed_response, savedNote)

  // Why a run came back with nothing to compare against. Without this an
  // uncompared result set looks exactly like one where nothing moved, which is
  // the opposite of what it means.
  const getComparisonNotice = () => {
    if (noteIsFromAnotherPlaybook) {
      return "This call ran on a different playbook, so its saved note isn't a comparison for this configuration."
    }
    if (isEmpty(savedNote)) {
      return 'This call has no saved note, so there is nothing to compare against. Every value below is new.'
    }
    if (isEmpty(previousValues)) {
      return "None of these fields are in this call's saved note, so there is nothing to compare them against."
    }
    return null
  }

  // On the call channel, split the results under the "Shown to Agent" /
  // "Processed Later" headers (mirroring the editor) by each field's
  // show_to_user; other channels render one flat list.
  const renderResults = () => {
    const { parsed_response: parsedResponse, fieldErrors, fieldResults, fieldSkips } = testResponse
    // Each field's type as the editor has it — including a type changed but not
    // yet saved, since that's what the run just tested. Keyed the way the
    // connection rail keys its own copy of this map.
    // Null-prototype: the keys are author-entered field names, and one called
    // `__proto__` would otherwise write through the prototype setter.
    const fieldTypeKeys = Object.create(null)
    values.prompt_fields.forEach((field) => {
      fieldTypeKeys[normalizeFieldKey(field.key)] = getRowTypeKey(field)
    })

    if (isMessage) {
      return (
        <AgentSummaryView
          key={runCount}
          parsedSummary={parsedResponse}
          fieldErrors={fieldErrors}
          fieldResults={fieldResults}
          fieldSkips={fieldSkips}
          previousValues={previousValues}
          fieldTypeKeys={fieldTypeKeys}
        />
      )
    }

    const shownByFieldName = Object.create(null)
    values.prompt_fields.forEach((field) => {
      shownByFieldName[field.key] = field.show_to_user === true
    })

    return RESULT_GROUPS.map((group) => {
      const groupSummary = Object.keys(parsedResponse)
        .filter((fieldName) => (shownByFieldName[fieldName] === true) === group.isShown)
        .reduce((summary, fieldName) => {
          summary[fieldName] = parsedResponse[fieldName]
          return summary
        }, {})

      if (isEmpty(groupSummary)) {
        return null
      }
      return (
        <div
          key={group.id}
          className="rtn-test-results-group"
          data-testid={`test-results-group-${group.id}`}
        >
          <h3 className="rtn-test-results-group__title">{group.title}</h3>
          <AgentSummaryView
            key={runCount}
            parsedSummary={groupSummary}
            fieldErrors={fieldErrors}
            fieldResults={fieldResults}
            fieldSkips={fieldSkips}
            previousValues={previousValues}
            fieldTypeKeys={fieldTypeKeys}
            groupId={group.id}
          />
        </div>
      )
    })
  }

  const comparisonNotice = testResponse?.parsed_response ? getComparisonNotice() : null

  return (
    <Drawer
      wide
      syncUrl={false}
      title={
        <span className="flex-align-center small-gap">
          <IconFlask />
          Test Notetaker
        </span>
      }
      trigger={(handleOpen) => (
        <Button
          type="button"
          icon={<IconFlask />}
          data-testid="test-changes-btn"
          onClick={handleOpen}
        >
          Test Changes
        </Button>
      )}
    >
      <div className="rtn-test-drawer">
        <p className="muted-text">
          Run a real interaction through the configured fields and see the note it produces.
        </p>

        <div className="rtn-test-drawer__run">
          <FormField>
            <label>{label}</label>
            <Field name="call_id" component={TextField} placeholder={placeholder} />
          </FormField>
          <Button
            primary
            type="button"
            icon={<IconPlayerPlay />}
            data-testid="run-test-btn"
            onClick={handleTestRTNPrompt}
            disabled={!values.call_id || hasErrors}
            disabledTooltip={tooltipMessage}
            loading={promptTestsLoading}
          >
            Run test
          </Button>
        </div>

        {promptTestsLoading ? (
          <div className="empty-rtn-response" data-testid="test-result-loader">
            <Loader />
          </div>
        ) : (
          testResponse?.parsed_response && (
            <div className="rtn-response-holder" data-testid="rtn-response-holder">
              {/* Say why the results stand alone, rather than leaving the reader
                  to read an uncompared run as an unchanged one. */}
              {comparisonNotice && (
                <Banner
                  informative
                  shrunk
                  icon={<IconInfoCircle />}
                  content={comparisonNotice}
                  dataTestId="comparison-notice"
                  className="rtn-test-drawer__notice"
                />
              )}
              {renderResults()}
            </div>
          )
        )}

        <RecentCallsList
          playbookCid={scopedPlaybookCid}
          callType={values.selected_channel}
          organizationId={organizationId}
          selectedCallId={values.call_id}
          onSelect={(callId) => setFieldValue('call_id', callId)}
        />
      </div>
    </Drawer>
  )
}
